import type { Middleware } from 'openapi-fetch';
import createClient from 'openapi-fetch';
import type { paths, AccountPutBody } from '@zenid/mypage-api';
import { auth0Client } from '#/utils/auth/auth0Client';

const authMiddleware: Middleware = {
  async onRequest(request) {
    const accessToken = await auth0Client.getTokenSilently();
    request.headers.set('Authorization', `Bearer ${accessToken}`);
    return request;
  },
};

type APIClient = ReturnType<typeof createClient<paths>>;

// Datadog RUM SDKの初期化後にfetchがインストルメントされるため、
// モジュール読み込み時ではなく最初のAPI呼び出し時にクライアントを作成する
let client: APIClient | undefined;

function getClient(): APIClient {
  if (!client) {
    client = createClient<paths>({ baseUrl: import.meta.env.VITE_API_URL });
    client.use(authMiddleware);
  }
  return client;
}

/**
 * ログインしているユーザーのユーザー情報を取得する
 *
 * TODO: mypage-apiのドキュメントに差し替える
 * @see https://special-adventure-vyo64mo.pages.github.io/#tag/accounts/operation/AccountApi_read
 */
export async function getAccount() {
  return await getClient().GET('/accounts');
}

/**
 * ログインしているユーザーの登録メールアドレスを変更する
 */
export async function putAccount(requestData: AccountPutBody) {
  return await getClient().PUT('/accounts', {
    body: requestData,
  });
}

/**
 * ログインしているユーザーの登録メールアドレスへ検証メールを再度送信する
 */
export async function postVerifyEmail() {
  return await getClient().POST('/verify-email');
}

/**
 * MFA登録URLを発行する
 */
export async function postAuthenticationMethodEnrollments() {
  return await getClient().POST('/authentication-method-enrollments');
}

/**
 * 登録済みのMFAの一覧を取得する
 */
export async function getAuthenticationMethods() {
  return await getClient().GET('/authentication-methods');
}

export async function postAccountIntegrateSendVerificationEmail(body: { email: string; callbackClientId?: string }) {
  return await getClient().POST('/accounts/integrate/send-verification-email', { body });
}

/**
 * ログインユーザーの学籍番号と一致する ZEN大学特別聴講生アカウントの有無を取得する
 */
export async function getAccountIntegrateSpecialAuditorAccount() {
  return await getClient().GET('/accounts/integrate/special-auditor-account');
}

export async function postAccountIntegrateExecuteWithToken(body: { token: string }) {
  return await getClient().POST('/accounts/integrate/execute-with-token', { body });
}

export async function postAccountIntegratePreserveAuthorization() {
  return await getClient().POST('/accounts/integrate/preserve-authorization', {
    credentials: 'include',
  });
}

export async function postAccountIntegrateExecuteWithExistingAccount() {
  return await getClient().POST('/accounts/integrate/execute-with-existing-account', {
    credentials: 'include',
    // openapi3 として cookie が表現可能だが、http only のものをリクエストパラメータとして渡すことは as never で暫定対処。根本対処には openapi-fetch の更新が必要
    params: {} as never,
  });
}

/**
 * 登録済みのMFAを削除する
 */
export async function deleteAuthenticationMethods(authenticationMethodId: string) {
  return await getClient().DELETE('/authentication-methods/{authenticationMethodId}', {
    params: {
      path: {
        authenticationMethodId,
      },
    },
  });
}
